import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import {
  Globe,
  ShieldCheck,
  TrendingUp,
  Clock,
  ArrowRight,
  Activity,
  CheckCircle,
  XCircle,
} from 'lucide-react'
import {
  getHealth,
  getDNSHealth,
  getCertHealth,
  listDNSRecords,
  listCertificates,
  resolveDNS,
} from '@/lib/api'
import { AppShell } from '@/components/layout/app-shell'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Spinner } from '@/components/ui/spinner'

function StatCard({
  title,
  value,
  icon: Icon,
  trend,
  linkTo,
}: {
  title: string
  value: string | number
  icon: React.ElementType
  trend?: string
  linkTo: string
}) {
  return (
    <Card className="hover:border-[var(--primary)] transition-colors">
      <Link to={linkTo}>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-[var(--fg-muted)]">{title}</CardTitle>
          <Icon className="h-4 w-4 text-[var(--fg-muted)]" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-[var(--fg)]">{value}</div>
          {trend && (
            <p className="text-xs text-[var(--fg-muted)] mt-1 flex items-center gap-1">
              <TrendingUp className="h-3 w-3" />
              {trend}
            </p>
          )}
        </CardContent>
      </Link>
    </Card>
  )
}

function DNSTestingCard() {
  const [hostname, setHostname] = useState('')
  const [testResult, setTestResult] = useState<{
    hostname: string
    resolved: boolean
    records: string[]
    error?: string | null
  } | null>(null)
  const [testing, setTesting] = useState(false)

  const handleTest = async () => {
    if (!hostname) return
    setTesting(true)
    setTestResult(null)

    try {
      const result = await resolveDNS(hostname)
      setTestResult(result)
    } catch (error) {
      setTestResult({
        hostname,
        resolved: false,
        records: [],
        error: error instanceof Error ? error.message : 'Unknown error',
      })
    } finally {
      setTesting(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Activity className="h-5 w-5 text-[var(--primary)]" />
          DNS Resolution Testing
        </CardTitle>
        <CardDescription>
          Test DNS resolution for any hostname to verify your DNS records
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          <Input
            placeholder="Enter hostname (e.g., api.dev.aip.cloud.teradata.com)"
            value={hostname}
            onChange={(e) => setHostname(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleTest()}
            disabled={testing}
          />
          <Button onClick={handleTest} disabled={testing || !hostname}>
            {testing ? <Spinner size="sm" /> : 'Test'}
          </Button>
        </div>

        {testResult && (
          <div
            className={`rounded-lg border p-4 ${
              testResult.resolved
                ? 'border-green-500/20 bg-green-500/10'
                : 'border-red-500/20 bg-red-500/10'
            }`}
          >
            <div className="flex items-start gap-3">
              {testResult.resolved ? (
                <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 shrink-0 mt-0.5" />
              ) : (
                <XCircle className="h-5 w-5 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
              )}
              <div className="flex-1 space-y-1">
                <p className="font-medium text-sm">
                  {testResult.resolved ? 'Resolution Successful' : 'Resolution Failed'}
                </p>
                <p className="text-xs text-[var(--fg-muted)]">
                  {testResult.hostname}
                </p>
                {testResult.records && testResult.records.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {testResult.records.map((record, i) => (
                      <code
                        key={i}
                        className="block text-xs bg-[var(--bg-elevated)] px-2 py-1 rounded"
                      >
                        {record}
                      </code>
                    ))}
                  </div>
                )}
                {testResult.error && (
                  <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                    {testResult.error}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="text-xs text-[var(--fg-muted)] space-y-1">
          <p>💡 Tip: Use this to verify newly created DNS records</p>
          <p>⚡ Resolution happens through the server's DNS resolver (not HTTP/HTTPS)</p>
        </div>
      </CardContent>
    </Card>
  )
}

function ServiceStatusCard() {
  const healthQuery = useQuery({
    queryKey: ['health'],
    queryFn: getHealth,
  })

  const dnsHealthQuery = useQuery({
    queryKey: ['dns-health'],
    queryFn: getDNSHealth,
  })

  const certHealthQuery = useQuery({
    queryKey: ['cert-health'],
    queryFn: getCertHealth,
  })

  return (
    <Card>
      <CardHeader>
        <CardTitle>Service Status</CardTitle>
        <CardDescription>Real-time health status of all services</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm text-[var(--fg-muted)]">Main Service</span>
          {healthQuery.isLoading ? (
            <Spinner size="sm" />
          ) : healthQuery.isSuccess ? (
            <Badge variant="success">Healthy</Badge>
          ) : (
            <Badge variant="error">Down</Badge>
          )}
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-[var(--fg-muted)]">DNS Service</span>
          {dnsHealthQuery.isLoading ? (
            <Spinner size="sm" />
          ) : dnsHealthQuery.isSuccess && dnsHealthQuery.data.status === 'healthy' ? (
            <Badge variant="success">Healthy</Badge>
          ) : (
            <Badge variant="error">Degraded</Badge>
          )}
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-[var(--fg-muted)]">Certificate Service</span>
          {certHealthQuery.isLoading ? (
            <Spinner size="sm" />
          ) : certHealthQuery.isSuccess && certHealthQuery.data.status === 'healthy' ? (
            <Badge variant="success">Healthy</Badge>
          ) : (
            <Badge variant="error">Down</Badge>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

export default function Dashboard() {
  const dnsQuery = useQuery({
    queryKey: ['dns-records', 'public'],
    queryFn: () => listDNSRecords('public'),
  })

  const certQuery = useQuery({
    queryKey: ['certificates'],
    queryFn: () => listCertificates(),
  })

  const dnsCount = dnsQuery.data?.total || 0
  const certCount = certQuery.data?.total || 0

  return (
    <AppShell title="Dashboard">
      <div className="space-y-6">
        {/* Stats Grid */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <StatCard
            title="DNS Records"
            value={dnsQuery.isLoading ? '-' : dnsCount}
            icon={Globe}
            trend="Active records"
            linkTo="/dns"
          />
          <StatCard
            title="Certificates"
            value={certQuery.isLoading ? '-' : certCount}
            icon={ShieldCheck}
            trend="Managed certificates"
            linkTo="/certificates"
          />
          <StatCard
            title="Public Zone · Public NLB"
            value="Active"
            icon={TrendingUp}
            trend="Public Route53 zone"
            linkTo="/dns"
          />
          <StatCard
            title="Public Zone · Private NLB & Private Link EP"
            value="Active"
            icon={Clock}
            trend="Public Route53 zone"
            linkTo="/dns"
          />
        </div>

        {/* DNS Testing & Service Status */}
        <div className="grid gap-6 lg:grid-cols-2">
          <DNSTestingCard />
          <ServiceStatusCard />
        </div>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
            <CardDescription>Common tasks to get you started</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-3 md:grid-cols-2">
            <Link to="/dns">
              <Button variant="outline" className="w-full justify-between">
                <span className="flex items-center gap-2">
                  <Globe className="h-4 w-4" />
                  Create DNS Record
                </span>
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
            <Link to="/certificates">
              <Button variant="outline" className="w-full justify-between">
                <span className="flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4" />
                  Enroll Certificate
                </span>
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    </AppShell>
  )
}
